/**
 * `<TerrainViewSwitch />` — MapLibre (2D map) ↔ Giro3D (3D terrain) toggle.
 *
 * Shown on elevation scenes (terrain slot or DEM/DTM rasters). OpenLayers
 * Canvas and deck.gl count as the 2D side so the control stays visible when
 * the session fell back off MapLibre.
 *
 * Visual contract mirrors `<PointCloudModeSwitch />`: two pressable chips
 * with `aria-pressed`, tokens only, 40 px touch min.
 */

import type { CSSProperties } from 'react';
import { Chip, Icon, tokens, type ChipVariant } from '@/design';
import type { EngineId } from '@geosoft/viewer-sdk';
import { isTerrainView2DEngine } from '@/components/map/terrainViewGate';
import { canRunWebGlEngines, isSoftwareWebGlSession } from '@/utils/webglCapability';

export type TerrainViewMode = 'maplibre' | 'giro3d';

export interface TerrainViewSwitchProps {
  /** Active engine. 2D engines highlight "2D map"; Giro3D highlights "3D view". */
  current: EngineId;
  /** Invoked when the operator picks the inactive mode. */
  onChange: (engine: TerrainViewMode) => void;
  /** Suppress clicks while ViewerHost remounts. */
  disabled?: boolean;
  /**
   * WebGL verdicts. Default to the session probe so callers get the right
   * behaviour for free; tests pass them explicitly because jsdom has no
   * WebGL context at all.
   */
  threeDUnavailable?: boolean;
  softwareRendering?: boolean;
  'data-testid'?: string;
}

interface ModeMeta {
  readonly id: TerrainViewMode;
  readonly label: string;
  readonly icon: 'map' | 'view_in_ar';
  readonly description: string;
}

const MODES: readonly ModeMeta[] = [
  {
    id: 'maplibre',
    label: '2D map',
    icon: 'map',
    description: 'Top-down MapLibre view',
  },
  {
    id: 'giro3d',
    label: '3D view',
    icon: 'view_in_ar',
    description: 'Tilt and orbit terrain in Giro3D',
  },
];

function modeIsPressed(mode: TerrainViewMode, current: EngineId): boolean {
  if (mode === 'maplibre') return isTerrainView2DEngine(current);
  return current === 'giro3d';
}

export function TerrainViewSwitch({
  current,
  onChange,
  disabled = false,
  // A software rasteriser still renders 3D, just slowly — say so rather than
  // letting the operator wonder why orbiting crawls. With no context at all
  // there is nothing to switch to.
  threeDUnavailable = !canRunWebGlEngines(),
  softwareRendering = isSoftwareWebGlSession(),
  'data-testid': testId,
}: TerrainViewSwitchProps) {
  const groupStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.space[1],
    padding: tokens.space[1],
    background: tokens.color.surface[1],
    borderRadius: tokens.radius.md,
    border: tokens.border.thin,
    minHeight: tokens.density.touchMin,
  };

  return (
    <section
      aria-label="Map dimension"
      data-testid={testId ?? 'terrain-view-switch'}
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: tokens.space[2],
      }}
    >
      <div role="group" aria-label="2D or 3D view" style={groupStyle}>
        {MODES.map((mode) => {
          const pressed = modeIsPressed(mode.id, current);
          const variant: ChipVariant = pressed ? 'info' : 'neutral';
          const unavailable = mode.id === 'giro3d' && threeDUnavailable;
          return (
            <Chip
              key={mode.id}
              label={mode.label}
              icon={mode.icon}
              variant={variant}
              pressable
              pressed={pressed}
              onClick={() => {
                if (disabled || unavailable) return;
                if (modeIsPressed(mode.id, current) && mode.id === current) return;
                // Already on a 2D engine (e.g. OpenLayers) and operator
                // re-clicks "2D map" — no-op unless they want MapLibre
                // specifically. Prefer leaving Canvas alone when already 2D.
                if (mode.id === 'maplibre' && isTerrainView2DEngine(current)) return;
                if (mode.id === 'giro3d' && current === 'giro3d') return;
                onChange(mode.id);
              }}
              aria-label={
                unavailable
                  ? `${mode.label} — unavailable: this host has no WebGL`
                  : `${mode.label} — ${mode.description}`
              }
              data-testid={`terrain-view-switch-${mode.id}`}
            />
          );
        })}
      </div>
      {softwareRendering && !disabled ? (
        <span
          data-testid="terrain-view-switch-software-gl"
          style={{
            fontFamily: tokens.font.family.ui,
            fontSize: tokens.font.role.label.size,
            color: tokens.color.ink.secondary,
            display: 'inline-flex',
            alignItems: 'center',
            gap: tokens.space[1],
          }}
        >
          <Icon name="info" size={16} decorative />
          Software rendering — 3D runs in reduced quality
        </span>
      ) : null}
      {disabled ? (
        <span
          role="status"
          aria-live="polite"
          data-testid="terrain-view-switch-switching"
          style={{
            fontFamily: tokens.font.family.ui,
            fontSize: tokens.font.role.label.size,
            color: tokens.color.ink.secondary,
            display: 'inline-flex',
            alignItems: 'center',
            gap: tokens.space[1],
          }}
        >
          <Icon name="pending" size={16} decorative />
          Switching view…
        </span>
      ) : null}
    </section>
  );
}
